import { Link } from "react-router-dom";
import {
  Mail, MapPin, Phone, Shield, Truck, CreditCard, Sparkles,
  Facebook, Instagram, Twitter, Youtube, ArrowUp,
} from "lucide-react";
import { useI18n } from "@/contexts/I18nContext";
import { SITE_CONFIG } from "@/config/site-meta";
import { BrandLogo } from "@/components/brand/BrandLogo";
const Footer = () => {
  const { t } = useI18n();
  const year = new Date().getFullYear();

  const trustBadges = [
    { icon: Shield, label: "Pago seguro", desc: "Stripe & SSL" },
    { icon: Truck, label: "Envío España", desc: "Packlink integrado" },
    { icon: CreditCard, label: "100% verificados", desc: "Identidad DNI/NIE" },
    { icon: Sparkles, label: "Sólo homologados", desc: "Certificación DGT" },
  ];

  const explore = [
    { to: "/buscar", label: t("footer.search") },
    { to: "/buscar?condicion=usado", label: "Patinetes ocasión" },
    { to: "/buscar?condicion=nuevo", label: "Patinetes nuevos" },
    { to: "/buscar?dgt=1", label: "Homologados DGT" },
    { to: "/buscar?categoria=recambios", label: "Recambios" },
    { to: "/buscar?categoria=accesorios", label: "Accesorios" },
  ];

  const tools = [
    { to: "/publicar", label: t("footer.publish") },
    { to: "/comparador", label: t("nav.comparator") },
    { to: "/guia-compra", label: t("nav.guide") },
    { to: "/calculadora", label: t("nav.calculator") },
    { to: "/blog", label: t("nav.blog") },
    { to: "/contacto", label: t("footer.contactUs") },
  ];

  const legal = [
    { to: "/privacidad", label: t("footer.privacy") },
    { to: "/terminos", label: t("footer.terms") },
    { to: "/aviso-legal", label: t("footer.legalNotice") },
  ];

  const socials = [
    { icon: Instagram, href: "https://instagram.com", label: "Instagram" },
    { icon: Facebook, href: "https://facebook.com", label: "Facebook" },
    { icon: Twitter, href: "https://twitter.com", label: "Twitter" },
    { icon: Youtube, href: "https://youtube.com", label: "YouTube" },
  ];

  return (
    <footer className="relative mt-16 overflow-hidden border-t border-border bg-card">
      {/* Decorative gradient blobs */}
      <div aria-hidden className="pointer-events-none absolute -top-32 left-1/4 h-72 w-72 rounded-full bg-primary/10 blur-3xl" />
      <div aria-hidden className="pointer-events-none absolute -bottom-40 right-10 h-80 w-80 rounded-full bg-accent/10 blur-3xl" />

      {/* Trust badges strip */}
      <div className="relative border-b border-border/60 bg-gradient-to-r from-muted/40 via-background to-muted/40">
        <div className="container grid grid-cols-2 gap-3 py-6 md:grid-cols-4">
          {trustBadges.map(({ icon: Icon, label, desc }) => (
            <div key={label} className="flex items-center gap-3">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
                <Icon className="h-5 w-5" />
              </div>
              <div className="leading-tight">
                <p className="text-sm font-semibold text-foreground">{label}</p>
                <p className="text-xs text-muted-foreground">{desc}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Main footer */}
      <div className="container relative grid gap-10 py-14 lg:grid-cols-12 lg:gap-8">
        {/* Brand */}
        <div className="lg:col-span-4">
          <Link to="/" className="inline-flex items-center gap-2.5 group">
            <BrandLogo size="lg" showTag className="origin-left scale-[0.85] transition-transform group-hover:-translate-y-0.5" />
          </Link>
          <p className="mt-4 max-w-sm text-sm leading-relaxed text-muted-foreground">
            {t("footer.tagline")}
          </p>

          <ul className="mt-6 space-y-2.5 text-sm">
            <li className="flex items-center gap-2.5 text-muted-foreground">
              <Mail className="h-4 w-4 text-primary" />
              <a href="mailto:hola@patinetes-segundamano.es" className="hover:text-foreground">
                hola@patinetes-segundamano.es
              </a>
            </li>
            <li className="flex items-center gap-2.5 text-muted-foreground">
              <Phone className="h-4 w-4 text-primary" />
              <a href="tel:+34900000000" className="hover:text-foreground">+34 900 000 000</a>
            </li>
            <li className="flex items-center gap-2.5 text-muted-foreground">
              <MapPin className="h-4 w-4 text-primary" /> Barcelona · España
            </li>
          </ul>

          <div className="mt-6 flex items-center gap-2">
            {socials.map(({ icon: Icon, href, label }) => (
              <a
                key={label}
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={label}
                className="flex h-9 w-9 items-center justify-center rounded-full border border-border bg-background text-muted-foreground transition-all hover:border-primary/40 hover:bg-primary/10 hover:text-primary"
              >
                <Icon className="h-4 w-4" />
              </a>
            ))}
          </div>
        </div>

        {/* Link columns */}
        <div className="lg:col-span-2">
          <h3 className="mb-4 font-display text-sm font-semibold uppercase tracking-wider text-foreground">
            {t("footer.explore")}
          </h3>
          <ul className="space-y-2.5 text-sm">
            {explore.map((l) => (
              <li key={l.to}>
                <Link to={l.to} className="text-muted-foreground transition-colors hover:text-primary">
                  {l.label}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        <div className="lg:col-span-2">
          <h3 className="mb-4 font-display text-sm font-semibold uppercase tracking-wider text-foreground">
            {t("footer.tools")}
          </h3>
          <ul className="space-y-2.5 text-sm">
            {tools.map((l) => (
              <li key={l.to}>
                <Link to={l.to} className="text-muted-foreground transition-colors hover:text-primary">
                  {l.label}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        {/* Métodos de pago */}
        <div className="lg:col-span-4">
          <h3 className="mb-4 font-display text-sm font-semibold uppercase tracking-wider text-foreground">
            Métodos de pago
          </h3>
          <p className="mb-4 text-sm text-muted-foreground">
            Compra con total tranquilidad. Aceptamos los principales métodos de pago seguros.
          </p>
          <div className="flex flex-wrap items-center gap-2 text-[11px] font-semibold text-muted-foreground">
            <span className="rounded-md border border-border bg-background px-2.5 py-1.5">VISA</span>
            <span className="rounded-md border border-border bg-background px-2.5 py-1.5">Mastercard</span>
            <span className="rounded-md border border-border bg-background px-2.5 py-1.5">PayPal</span>
            <span className="rounded-md border border-border bg-background px-2.5 py-1.5">Bizum</span>
            <span className="rounded-md border border-border bg-background px-2.5 py-1.5">Stripe</span>
            <span className="rounded-md border border-border bg-background px-2.5 py-1.5">Transferencia</span>
          </div>

          <div className="mt-6 rounded-xl border border-border bg-muted/40 p-4">
            <p className="text-xs font-semibold text-foreground">¿Necesitas ayuda?</p>
            <p className="mt-1 text-xs text-muted-foreground">
              Nuestro equipo está disponible de Lunes a Viernes, 9:00 - 18:00.
            </p>
            <Link to="/contacto" className="mt-2 inline-block text-xs font-semibold text-primary hover:underline">
              Contactar →
            </Link>
          </div>
        </div>
      </div>

      {/* Bottom bar */}
      <div className="relative border-t border-border/60 bg-muted/30">
        <div className="container flex flex-col items-center justify-between gap-4 py-5 text-xs text-muted-foreground md:flex-row">
          <p>© {year} {SITE_CONFIG.name}. {t("footer.rights")}</p>
          <div className="flex flex-wrap items-center justify-center gap-x-5 gap-y-2">
            {legal.map((l) => (
              <Link key={l.to} to={l.to} className="transition-colors hover:text-foreground">
                {l.label}
              </Link>
            ))}
          </div>
          <button
            onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
            className="inline-flex items-center gap-1.5 rounded-full border border-border bg-background px-3 py-1.5 text-xs font-medium transition-all hover:border-primary/40 hover:text-primary"
            aria-label="Volver arriba"
          >
            <ArrowUp className="h-3 w-3" /> Arriba
          </button>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
